import React, { useState, useEffect, useRef } from 'react';
import Modal from './Modal';
import CalendarPicker from './CalendarPicker';
import CustomSelect from './CustomSelect';
import CompanySelect from './CompanySelect';
import AutocompleteInput from './AutocompleteInput';
import DocViewer from './DocViewer';
import { useFormData } from '../hooks/useFormData';

const getLeasingFileUrl = (filename) => {
  if (!filename) return null;
  const base = (process.env.REACT_APP_API_URL || 'http://localhost:4000').replace(/\/api\/?$/, '');
  return `${base}/uploads/leasing/${filename}`;
};

const LeasingForm = ({ isOpen, onClose, leasing, onSave, defaultPlate, defaultCompany }) => {
  const [formData, setFormData] = useState({
    plate: '', leasingCompany: '', contractNumber: '',
    startDate: '', endDate: '', monthlyInstallment: '',
    totalValue: '', residualValue: '', currency: 'RON',
    status: 'active', company: '', notes: '',
  });
  const [files, setFiles] = useState([]);
  const [dragOver, setDragOver] = useState(false);
  const [previewDoc, setPreviewDoc] = useState(null);
  const [loading, setLoading] = useState(false);
  const fileInputRef = useRef(null);

  useEffect(() => {
    setFiles([]);
    if (leasing) {
      setFormData({
        plate: leasing.plate || '',
        leasingCompany: leasing.leasingCompany || '',
        contractNumber: leasing.contractNumber || '',
        startDate: leasing.startDate ? leasing.startDate.slice(0, 10) : '',
        endDate: leasing.endDate ? leasing.endDate.slice(0, 10) : '',
        monthlyInstallment: leasing.monthlyInstallment ?? '',
        totalValue: leasing.totalValue ?? '',
        residualValue: leasing.residualValue ?? '',
        currency: leasing.currency || 'RON',
        status: leasing.status || 'active',
        company: leasing.company || '',
        notes: leasing.notes || '',
      });
    } else {
      setFormData({
        plate: defaultPlate || '', leasingCompany: '', contractNumber: '',
        startDate: '', endDate: '', monthlyInstallment: '',
        totalValue: '', residualValue: '', currency: 'RON',
        status: 'active', company: defaultCompany || '', notes: '',
      });
    }
  }, [leasing, isOpen, defaultPlate, defaultCompany]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleFilesSelect = (selected) => {
    const list = Array.from(selected || []);
    if (!list.length) return;
    const valid = [];
    for (const f of list) {
      if (!/\.(pdf|jpg|jpeg|png)$/i.test(f.name)) {
        alert(`${f.name}: only PDF, JPG, and PNG files are allowed.`);
        continue;
      }
      if (f.size > 10 * 1024 * 1024) {
        alert(`${f.name}: file size must be under 10MB.`);
        continue;
      }
      valid.push(f);
    }
    setFiles((prev) => [...prev, ...valid]);
  };

  const handleRemoveFile = (idx) => setFiles((prev) => prev.filter((_, i) => i !== idx));

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    handleFilesSelect(e.dataTransfer.files);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await onSave({
        ...formData,
        monthlyInstallment: Number(formData.monthlyInstallment),
        totalValue: formData.totalValue !== '' ? Number(formData.totalValue) : 0,
        residualValue: formData.residualValue !== '' ? Number(formData.residualValue) : 0,
      }, files.length ? files : undefined);
      onClose();
    } catch (err) {
      alert('Error saving leasing contract. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const isEditing = !!(leasing && leasing._id);
  const fd = useFormData();
  const existingFiles = leasing?.files?.length ? leasing.files : (leasing?.fileUrl ? [{ fileUrl: leasing.fileUrl, fileOrigName: leasing.fileOrigName }] : []);

  return (
    <>
    <Modal isOpen={isOpen} onClose={onClose} title={isEditing ? 'Edit Leasing Contract' : 'Add Leasing Contract'} size="md">
      <form onSubmit={handleSubmit}>
        <div className="form-row">
          <div className="form-group">
            <label className="form-label">Vehicle Plate *</label>
            <AutocompleteInput name="plate" value={formData.plate} onChange={handleChange}
              suggestions={fd.plates} required placeholder="e.g. B-12-XYZ" />
          </div>
          <div className="form-group">
            <label className="form-label">Leasing Company *</label>
            <AutocompleteInput name="leasingCompany" value={formData.leasingCompany} onChange={handleChange}
              suggestions={fd.leasingCompanies} required placeholder="e.g. BCR Leasing" />
          </div>
        </div>

        <div className="form-row">
          <div className="form-group">
            <label className="form-label">Contract Number</label>
            <input type="text" name="contractNumber" className="form-input" value={formData.contractNumber} onChange={handleChange} placeholder="e.g. LCT-2024-001" />
          </div>
          <div className="form-group">
            <label className="form-label">Status</label>
            <CustomSelect name="status" className="form-select" value={formData.status} onChange={handleChange}>
              <option value="active">Active</option>
              <option value="completed">Completed</option>
              <option value="terminated">Terminated</option>
            </CustomSelect>
          </div>
        </div>

        <div className="form-row">
          <div className="form-group">
            <label className="form-label">Start Date *</label>
            <CalendarPicker name="startDate" value={formData.startDate} onChange={handleChange} required />
          </div>
          <div className="form-group">
            <label className="form-label">End Date *</label>
            <CalendarPicker name="endDate" value={formData.endDate} onChange={handleChange} required />
          </div>
        </div>

        <div className="form-row">
          <div className="form-group">
            <label className="form-label">Monthly Installment *</label>
            <input type="number" name="monthlyInstallment" className="form-input" value={formData.monthlyInstallment} onChange={handleChange} required placeholder="0.00" min="0" step="0.01" />
          </div>
          <div className="form-group">
            <label className="form-label">Currency</label>
            <CustomSelect name="currency" className="form-select" value={formData.currency} onChange={handleChange}>
              <option value="RON">RON</option>
              <option value="EUR">EUR</option>
            </CustomSelect>
          </div>
        </div>

        <div className="form-row">
          <div className="form-group">
            <label className="form-label">Total Contract Value</label>
            <input type="number" name="totalValue" className="form-input" value={formData.totalValue} onChange={handleChange} placeholder="0.00" min="0" step="0.01" />
          </div>
          <div className="form-group">
            <label className="form-label">Residual Value</label>
            <input type="number" name="residualValue" className="form-input" value={formData.residualValue} onChange={handleChange} placeholder="0.00" min="0" step="0.01" />
          </div>
        </div>

        <div className="form-row">
          <div className="form-group">
            <label className="form-label">Fleet Company</label>
            <CompanySelect name="company" value={formData.company} onChange={handleChange} />
          </div>
          <div className="form-group">
            <label className="form-label">Notes</label>
            <input type="text" name="notes" className="form-input" value={formData.notes} onChange={handleChange} placeholder="Additional notes" />
          </div>
        </div>

        {/* Contract Document Upload */}
        <div className="form-group" style={{ marginTop: 8 }}>
          <label className="form-label">Contract Documents</label>

          {files.length > 0 && (
            <div style={{ display: 'grid', gap: 8, marginBottom: 8 }}>
              {files.map((f, idx) => (
                <div key={`${f.name}-${idx}`} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '12px 16px', background: '#f0f9ff', border: '1px solid #bae6fd', borderRadius: 10 }}>
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#0077b6" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z" /><polyline points="14 2 14 8 20 8" />
                  </svg>
                  <span style={{ flex: 1, fontSize: 13, color: '#0077b6', fontWeight: 600 }}>{f.name}</span>
                  <span style={{ fontSize: 12, color: '#64748b' }}>{(f.size / 1024).toFixed(0)} KB</span>
                  <button type="button" onClick={() => handleRemoveFile(idx)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#94a3b8', padding: 2 }}>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
                  </button>
                </div>
              ))}
            </div>
          )}

          <div
            onClick={() => fileInputRef.current?.click()}
            onDrop={handleDrop}
            onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
            onDragLeave={() => setDragOver(false)}
            style={{ border: `2px dashed ${dragOver ? '#0077b6' : '#cbd5e1'}`, borderRadius: 10, padding: '24px 20px', textAlign: 'center', cursor: 'pointer', background: dragOver ? '#f0f9ff' : '#f8fafc', transition: 'all 0.15s' }}
          >
            <div style={{ fontSize: 14, fontWeight: 600, color: '#0f172a', marginBottom: 4 }}>Click or drag files here</div>
            <div style={{ fontSize: 12, color: '#94a3b8' }}>PDF, JPG, PNG — max 10MB each, multiple allowed</div>
          </div>

          {isEditing && existingFiles.length > 0 && (
            <div style={{ marginTop: 8, display: 'grid', gap: 6 }}>
              {existingFiles.map((f, idx) => (
                <div key={f.fileUrl || idx} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, color: '#64748b' }}>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21.44 11.05l-9.19 9.19a6 6 0 01-8.49-8.49l9.19-9.19a4 4 0 015.66 5.66l-9.2 9.19a2 2 0 01-2.83-2.83l8.49-8.48"/></svg>
                  Current:
                  <button
                    type="button"
                    onClick={() => setPreviewDoc({ url: getLeasingFileUrl(f.fileUrl), filename: f.fileOrigName || f.fileUrl })}
                    style={{ background: 'none', border: 'none', padding: 0, color: '#0077b6', fontWeight: 500, cursor: 'pointer' }}
                  >
                    {f.fileOrigName || f.fileUrl} — Preview
                  </button>
                </div>
              ))}
              <span style={{ color: '#94a3b8', fontSize: 12 }}>(uploading new files replaces all current documents)</span>
            </div>
          )}

          <input ref={fileInputRef} type="file" accept=".pdf,.jpg,.jpeg,.png" multiple style={{ display: 'none' }}
            onChange={(e) => { handleFilesSelect(e.target.files); e.target.value = ''; }} />
        </div>

        <p style={{ fontSize: 12, color: 'var(--text-3)', marginTop: 4 }}>
          The payment schedule is auto-generated from start date, end date and monthly installment.
        </p>

        <div className="form-actions">
          <button type="button" className="btn btn-ghost" onClick={onClose}>Cancel</button>
          <button type="submit" className="btn btn-primary" disabled={loading}>{loading ? 'Saving...' : isEditing ? 'Update Contract' : 'Add Contract'}</button>
        </div>
      </form>
    </Modal>
    <DocViewer url={previewDoc?.url} filename={previewDoc?.filename} onClose={() => setPreviewDoc(null)} />
    </>
  );
};

export default LeasingForm;
